import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "item", "amount", "total", "destroy", "amountInput", "descriptionRow"]
  static values = { taxRates: Array, taxCalculationMethod: String, itemTaxCalculationMethod: String }

  connect() {
    // 既存の明細の金額を計算
    this.itemTargets.forEach(item => {
      this.calculateItemAmount(item)
    })
    this.updateTotal()
  }

  addItem() {
    const newIndex = Date.now()
    const content = this.getItemTemplate().replace(/NEW_RECORD/g, newIndex)

    // コンテナの最後に新しい明細を追加
    this.containerTarget.insertAdjacentHTML('beforeend', content)
    this.updateTotal()
  }

  removeItem(event) {
    const item = event.target.closest('[data-order-items-target="item"]')
    const destroyField = item.querySelector('[data-order-items-target="destroy"]')

    if (destroyField) {
      // destroyFieldが存在する場合、name属性を確認
      const name = destroyField.name || ''

      // 既存レコードかどうかを判定（数字のIDが含まれているかチェック）
      const isExistingRecord = name.includes('[') && name.includes(']') &&
                              !/NEW_RECORD/.test(name) &&
                              /\[\d+\]/.test(name)

      if (isExistingRecord) {
        // 既存のレコードの場合は_destroyフラグをセット
        destroyField.value = '1'
        item.style.display = 'none'
      } else {
        // 新規レコードの場合は完全に削除
        item.remove()
      }
    } else {
      // destroyFieldが見つからない場合は完全に削除
      item.remove()
    }

    this.updateTotal()
  }

  calculateAmount(event) {
    const item = event.target.closest('[data-order-items-target="item"]')
    this.calculateItemAmount(item)
    this.updateTotal()
  }

  toggleDescription(event) {
    const item = event.target.closest('[data-order-items-target="item"]')
    const descriptionRow = item.querySelector('[data-order-items-target="descriptionRow"]')
    const button = event.target

    if (descriptionRow.classList.contains('hidden')) {
      descriptionRow.classList.remove('hidden')
      button.textContent = '閉じる'
      button.classList.remove('text-blue-600', 'hover:text-blue-800', 'hover:bg-blue-50')
      button.classList.add('text-gray-600', 'hover:text-gray-800', 'hover:bg-gray-50')
    } else {
      descriptionRow.classList.add('hidden')
      button.textContent = '詳細'
      button.classList.remove('text-gray-600', 'hover:text-gray-800', 'hover:bg-gray-50')
      button.classList.add('text-blue-600', 'hover:text-blue-800', 'hover:bg-blue-50')
    }
  }

  calculateItemAmount(item) {
    const quantityInput = item.querySelector('input[name*="[quantity]"]')
    const unitPriceInput = item.querySelector('input[name*="[unit_price]"]')
    const taxRateSelect = item.querySelector('select[name*="[tax_rate_id]"]')
    const amountDisplay = item.querySelector('[data-order-items-target="amount"]')
    const amountInput = item.querySelector('[data-order-items-target="amountInput"]')

    if (quantityInput && unitPriceInput && taxRateSelect && amountDisplay) {
      const quantity = parseFloat(quantityInput.value) || 0
      const unitPrice = parseFloat(unitPriceInput.value) || 0
      const taxRateId = parseInt(taxRateSelect.value) || 1

      // 税率を取得
      const taxRate = this.getTaxRate(taxRateId)

      // インボイス制度対応：税抜金額のみを計算
      const rawSubtotal = quantity * unitPrice
      const subtotal = this.roundAmount(rawSubtotal)

      // 税抜金額を表示
      amountDisplay.textContent = `¥${Math.floor(subtotal).toLocaleString()}（税抜）`

      // hidden fieldには税抜金額を保存
      if (amountInput) {
        amountInput.value = Math.floor(this.roundAmount(subtotal))
      }
    }
  }

  getTaxRate(taxRateId) {
    const taxRateObj = this.taxRatesValue.find(rateObj => {
      const rate = rateObj.attributes || rateObj
      return rate.id === taxRateId
    })
    if (taxRateObj) {
      const rate = taxRateObj.attributes || taxRateObj
      return rate.rate
    }
    return 10.0
  }


  taxRateOptions() {
    if (!this.taxRatesValue || !Array.isArray(this.taxRatesValue)) {
      // フォールバック用の税率オプション
      return `
        <option value="1" selected>10%</option>
        <option value="2">8%（軽減税率）</option>
        <option value="3">免税</option>
        <option value="4">非課税</option>
        <option value="5">不課税</option>
      `
    }
    return this.taxRatesValue.map(rateObj => {
      // ActiveHashのto_jsonは attributes でネストされる
      const rate = rateObj.attributes || rateObj
      const selected = rate.id === 1 ? 'selected' : ''
      return `<option value="${rate.id}" ${selected}>${rate.name}</option>`
    }).join('')
  }

  getTaxMethodText() {
    const method = this.taxCalculationMethodValue || 'floor'
    switch (method) {
      case 'ceil':
        return '切り上げ'
      case 'round':
        return '四捨五入'
      case 'floor':
      default:
        return '切り捨て'
    }
  }


  roundAmount(amount) {
    const method = this.taxCalculationMethodValue || 'floor'
    switch (method) {
      case 'ceil':
        return Math.ceil(amount)
      case 'round':
        return Math.round(amount)
      case 'floor':
      default:
        return Math.floor(amount)
    }
  }

  roundItemTax(amount) {
    const method = this.itemTaxCalculationMethodValue || 'floor'
    switch (method) {
      case 'ceil':
        return Math.ceil(amount)
      case 'round':
        return Math.round(amount)
      case 'floor':
      default:
        return Math.floor(amount)
    }
  }

  updateTotal() {
    // 税率ごとにグループ化して税額を計算（インボイス制度対応）
    const taxGroups = {}
    let totalExcludingTax = 0

    this.itemTargets.forEach(item => {
      if (item.style.display !== 'none') {
        const quantityInput = item.querySelector('input[name*="[quantity]"]')
        const unitPriceInput = item.querySelector('input[name*="[unit_price]"]')
        const taxRateSelect = item.querySelector('select[name*="[tax_rate_id]"]')

        if (quantityInput && unitPriceInput && taxRateSelect) {
          const quantity = parseFloat(quantityInput.value) || 0
          const unitPrice = parseFloat(unitPriceInput.value) || 0
          const taxRateId = parseInt(taxRateSelect.value) || 1

          // 税抜金額を計算
          const rawSubtotal = quantity * unitPrice
          const subtotal = this.roundAmount(rawSubtotal)
          totalExcludingTax += subtotal

          // 税率ごとにグループ化
          const taxRate = this.getTaxRate(taxRateId)
          if (!taxGroups[taxRateId]) {
            taxGroups[taxRateId] = { rate: taxRate, amount: 0 }
          }
          taxGroups[taxRateId].amount += subtotal
        }
      }
    })

    // 税率ごとに税額を計算
    let totalTax = 0
    Object.values(taxGroups).forEach(group => {
      if (group.rate > 0) {
        const taxAmount = this.roundItemTax(group.amount * group.rate / 100)
        totalTax += taxAmount
      }
    })

    // 税込合計を表示
    const totalIncludingTax = totalExcludingTax + totalTax

    if (this.hasTotalTarget) {
      this.totalTarget.innerHTML = `
        <div>¥${Math.floor(totalIncludingTax).toLocaleString()}</div>
        <div class="text-xs text-gray-600 mt-1">
          <div>税抜: ¥${Math.floor(totalExcludingTax).toLocaleString()}</div>
          <div>税額: ¥${Math.floor(totalTax).toLocaleString()}</div>
        </div>
      `
    }
  }

  getItemTemplate() {
    // 税率オプションを事前に生成
    const taxOptions = this.taxRateOptions()

    return `
    <div class="order-item-row" data-order-items-target="item">
      <!-- メイン行 -->
      <div class="grid grid-cols-1 gap-4 px-3 py-2 border-b border-gray-200 items-start sm:grid-cols-7 sm:gap-2">
        <!-- 項目名 -->
        <div class="order-1">
          <label class="block text-xs font-medium text-gray-700 mb-1 sm:hidden">項目名</label>
          <input type="text" name="order[order_items_attributes][NEW_RECORD][name]"
                 class="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                 placeholder="項目名を入力">
        </div>

        <!-- 数量 -->
        <div class="text-left order-2 sm:text-center">
          <label class="block text-xs font-medium text-gray-700 mb-1 sm:hidden">数量</label>
          <input type="number" name="order[order_items_attributes][NEW_RECORD][quantity]"
                 class="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm text-left sm:text-right"
                 step="0.1" min="0" value="1"
                 data-action="input->order-items#calculateAmount">
        </div>

        <!-- 単位 -->
        <div class="text-left order-3 sm:text-center">
          <label class="block text-xs font-medium text-gray-700 mb-1 sm:hidden">単位</label>
          <input type="text" name="order[order_items_attributes][NEW_RECORD][unit]"
                 class="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm text-left sm:text-center"
                 placeholder="個">
        </div>

        <!-- 単価 -->
        <div class="text-left order-4 sm:text-center">
          <label class="block text-xs font-medium text-gray-700 mb-1 sm:hidden">単価（税抜）</label>
          <input type="number" name="order[order_items_attributes][NEW_RECORD][unit_price]"
                 class="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm text-left sm:text-right"
                 step="1" min="0"
                 data-action="input->order-items#calculateAmount">
        </div>

        <!-- 税区分 -->
        <div class="text-left order-5 sm:text-center">
          <label class="block text-xs font-medium text-gray-700 mb-1 sm:hidden">税区分</label>
          <select name="order[order_items_attributes][NEW_RECORD][tax_rate_id]"
                  class="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  data-action="change->order-items#calculateAmount">
            ${taxOptions}
          </select>
        </div>

        <!-- 金額 -->
        <div class="text-left order-6 sm:text-right">
          <label class="block text-xs font-medium text-gray-700 mb-1 sm:hidden">金額</label>
          <div class="text-sm font-medium text-gray-900" data-order-items-target="amount">
            ¥0（税抜）
          </div>
          <input type="hidden" name="order[order_items_attributes][NEW_RECORD][amount]" value="0" data-order-items-target="amountInput">
        </div>

        <!-- 操作 -->
        <div class="text-left order-7 sm:text-center">
          <label class="block text-xs font-medium text-gray-700 mb-1 sm:hidden">操作</label>
          <div class="flex gap-1">
            <button type="button"
                    class="text-blue-600 hover:text-blue-800 text-sm font-medium px-2 py-1 rounded hover:bg-blue-50"
                    data-action="click->order-items#toggleDescription"
                    title="詳細説明">
              詳細
            </button>
            <button type="button"
                    class="text-red-600 hover:text-red-800 text-sm font-medium px-2 py-1 rounded hover:bg-red-50"
                    data-action="click->order-items#removeItem"
                    title="削除">
              削除
            </button>
          </div>
        </div>
      </div>

      <!-- 詳細説明行（初期状態では非表示） -->
      <div class="hidden px-3 py-2 bg-gray-50 border-b border-gray-200" data-order-items-target="descriptionRow">
        <label class="block text-xs font-medium text-gray-700 mb-1">詳細説明</label>
        <textarea name="order[order_items_attributes][NEW_RECORD][description]"
                  class="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm resize-y"
                  placeholder="詳細説明を入力してください"
                  rows="3"></textarea>
      </div>

      <input type="hidden" name="order[order_items_attributes][NEW_RECORD][_destroy]" value="" data-order-items-target="destroy">
    </div>`
  }
}
